import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Alert,
  AlertTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Warning,
  CheckCircle,
  Error,
  CalendarToday,
  Description,
  Gavel,
  AccountBalance,
  AttachMoney,
  Schedule,
  NotificationsActive
} from '@mui/icons-material';
import { ComplianceService } from '../../services/ComplianceService';
import { DocumentService } from '../../services/integrations/DocumentService';
import { NotificationService } from '../../services/integrations/NotificationService';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { ComplianceTimeline } from '../ComplianceTimeline';
import { DocumentUploader } from '../../components/SmartComply/DocumentManager/DocumentUploader';
import { AuditLogger } from '../../utils/AuditLogger';
import {
  DocumentType,
  RetentionPolicy,
  SensitivityLevel,
  DocumentMetadata,
  DocumentStatus
} from '../../types/documents';
import {
  ComplianceRequirement,
  ComplianceThreshold,
  ComplianceStatus,
  ComplianceType
} from '../../types/compliance';

interface ComplianceMonitorProps {
  fundId: string;
  stateRequirements: Record<string, any>;
  federalRequirements: Record<string, any>;
}

export const ComplianceMonitor: React.FC<ComplianceMonitorProps> = ({
  fundId,
  stateRequirements,
  federalRequirements
}) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [requirements, setRequirements] = useState<ComplianceRequirement[]>([]);
  const [thresholds, setThresholds] = useState<ComplianceThreshold[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<ComplianceRequirement | null>(null);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [alerts, setAlerts] = useState<Array<{
    type: 'error' | 'warning' | 'info';
    message: string;
    action?: () => void;
  }>>([]);

  const complianceService = new ComplianceService();
  const documentService = new DocumentService();
  const notificationService = new NotificationService();

  const handleValidationResults = (validation: any) => {
    if (validation.issues?.length > 0) {
      setAlerts(prev => [
        ...prev,
        ...validation.issues.map((issue: any) => ({
          type: issue.severity as 'error' | 'warning' | 'info',
          message: issue.message
        }))
      ]);
    }
  };

  const handleThresholdCrossed = async (threshold: ComplianceThreshold) => {
    await AuditLogger.log('THRESHOLD_CROSSED', {
      action: 'THRESHOLD_CROSSED',
      fundId,
      details: threshold
    });

    const updatedReqs = await complianceService.updateRequirements(fundId, {
      ...threshold,
      id: threshold.id || '',
      amount: threshold.amount || 0,
      period: threshold.period || '',
      status: threshold.status || '',
      action: threshold.action || ''
    });
    setRequirements(updatedReqs);

    await notificationService.sendThresholdAlert(fundId, threshold);
  };

  const handleDeadlineApproaching = async (requirement: ComplianceRequirement) => {
    const requirementWithEvents = {
      ...requirement,
      events: requirement.events || []
    };
    
    await notificationService.sendDeadlineReminder(fundId, requirementWithEvents);
    setAlerts(prev => [...prev, {
      type: 'warning',
      message: `Upcoming deadline: ${requirement.name} due on ${formatDate(requirement.dueDate)}`,
      action: () => handleRequirementClick(requirementWithEvents)
    }]);
  };

  const handleComplianceChange = async (change: any) => {
    await AuditLogger.log('COMPLIANCE_CHANGE', {
      action: 'COMPLIANCE_CHANGE',
      fundId,
      details: change
    });
    await loadRequirements();
  };

  const handleDocumentExpiring = async (document: any) => {
    await notificationService.sendDocumentExpirationAlert(fundId, document);
    setAlerts(prev => [...prev, {
      type: 'warning',
      message: `Document expiring: ${document.name}`,
      action: () => setDocumentDialogOpen(true)
    }]);
  };

  const handleDocumentUpload = async (files: File[]) => {
    if (!fundId) {
      setAlerts(prev => [...prev, {
        type: 'error',
        message: 'No fund ID provided'
      }]);
      return;
    }

    try {
      for (const file of files) {
        const metadata: DocumentMetadata = {
          type: DocumentType.COMPLIANCE_DOCUMENT,
          requirementId: selectedRequirement?.id || '',
          status: DocumentStatus.ACTIVE,
          fiscalSponsorId: fundId,
          category: 'COMPLIANCE',
          tags: ['compliance', 'requirement'],
          retention: RetentionPolicy.SEVEN_YEARS,
          sensitivity: SensitivityLevel.CONFIDENTIAL,
          name: file.name,
          description: `Compliance document for requirement: ${selectedRequirement?.name || 'Unknown'}`,
          createdAt: new Date(),
          updatedAt: new Date(),
          version: 1
        };
        
        await documentService.uploadDocument(file, metadata);
      }
      await loadRequirements();
      setDocumentDialogOpen(false);
    } catch (error) {
      console.error('Failed to upload document:', error);
      setAlerts(prev => [...prev, {
        type: 'error',
        message: 'Failed to upload document'
      }]);
    }
  };

  const scheduleUpcomingDeadlines = (requirements: ComplianceRequirement[]) => {
    requirements.forEach(req => {
      const daysUntilDue = Math.ceil((new Date(req.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntilDue <= 30) {
        handleDeadlineApproaching(req);
      }
    });
  };

  const monitorThresholds = (thresholds: ComplianceThreshold[]) => {
    thresholds.forEach(threshold => {
      // Implementation for threshold monitoring
    });
  };

  const loadRequirements = async () => {
    const reqs = await complianceService.getRequirements(fundId);
    setRequirements(reqs);
    scheduleUpcomingDeadlines(reqs);
  };

  const loadThresholds = async () => {
    const thresh = await complianceService.getThresholds(fundId);
    setThresholds(thresh);
    monitorThresholds(thresh);
  };

  const setupAutomatedMonitoring = async () => {
    await complianceService.setupMonitoring(fundId, {
      onThresholdCrossed: handleThresholdCrossed,
      onDeadlineApproaching: handleDeadlineApproaching,
      onComplianceChange: handleComplianceChange,
      onDocumentExpiring: handleDocumentExpiring
    });
  };

  const validateCurrentCompliance = async () => {
    const validation = await complianceService.validateCompliance(fundId);
    handleValidationResults(validation);
  };

  const handleRequirementClick = (requirement: ComplianceRequirement) => {
    setSelectedRequirement(requirement);
    setTimelineOpen(true);
  };

  const renderComplianceStatus = (category: string) => {
    const categoryRequirements = requirements.filter(req => 
      req.type.toLowerCase().includes(category.toLowerCase())
    );
    
    const compliantCount = categoryRequirements.filter(req => 
      req.status === 'COMPLIANT'
    ).length;

    const progress = categoryRequirements.length > 0 
      ? (compliantCount / categoryRequirements.length) * 100 
      : 0;

    return (
      <Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          color={progress === 100 ? 'success' : 'primary'}
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {compliantCount} of {categoryRequirements.length} requirements met
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    if (fundId) {
      initializeComplianceMonitoring();
    }
  }, [fundId]);

  const initializeComplianceMonitoring = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadRequirements(),
        loadThresholds(),
        setupAutomatedMonitoring(),
        validateCurrentCompliance()
      ]);
    } catch (error) {
      console.error('Failed to initialize compliance monitoring:', error);
      setAlerts(prev => [...prev, {
        type: 'error',
        message: 'Failed to initialize compliance monitoring'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Compliance Monitor
              {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Typography>
          </Grid>

          {/* Alerts Section */}
          {alerts.length > 0 && (
            <Grid item xs={12}>
              {alerts.map((alert, index) => (
                <Alert 
                  key={index}
                  severity={alert.type}
                  sx={{ mb: 1 }}
                  action={
                    alert.action && (
                      <Button color="inherit" size="small" onClick={alert.action}>
                        View
                      </Button>
                    )
                  }
                >
                  {alert.message}
                </Alert>
              ))}
            </Grid>
          )}

          {/* Compliance Status Cards */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {['Federal', 'State', 'Audit', 'Reporting'].map((category) => (
                <Grid item xs={12} md={3} key={category}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{category} Compliance</Typography>
                      {renderComplianceStatus(category)}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Requirements Table */}
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Requirement</TableCell>
                  <TableCell>Jurisdiction</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requirements.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell>{req.name}</TableCell>
                    <TableCell>{req.jurisdiction}</TableCell>
                    <TableCell>{formatDate(req.dueDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        color={
                          req.status === 'COMPLIANT' ? 'success' :
                          req.status === 'AT_RISK' ? 'warning' : 'error'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleRequirementClick(req)}>
                        <Description />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </Paper>

      {/* Compliance Timeline Dialog */}
      <ComplianceTimeline
        requirement={selectedRequirement}
        onClose={() => setTimelineOpen(false)}
        onUpdate={handleComplianceChange}
        isOpen={timelineOpen}
      />

      {/* Document Management Dialog */}
      <Dialog
        open={documentDialogOpen}
        onClose={() => setDocumentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Document Management</DialogTitle>
        <DialogContent>
          <DocumentUploader
            fundId={fundId}
            requirementId={selectedRequirement?.id}
            onUpload={handleDocumentUpload}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};